import { useOnMount, useOnUnmount } from '@prophecy/utils/react/hooks';
import { isUndefined } from 'lodash-es';
import { useState } from 'react';
const scriptMap = new Map();
/**
 * @description Loads a script and returns the loaded state and instance
 * @param src - The source URL of the script
 * @param onLoad - The callback function to be called when the script is loaded
 * @param onError - The callback function to be called when the script fails to load
 * @param removeOnUnmount - Whether to remove the script when all components that use it are unmounted
 * @param type - The type of the script
 * @param async - Whether to load the script asynchronously
 * @param defer - Whether to load the script defer
 * @param onUnmount - The callback function to be called when the script is unmounted
 * @param getInstance - The callback function to be called to get the instance of the script
 * @param integrity - The integrity of the script
 * @param crossOrigin - The cross origin of the script
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useScript = ({ src, onLoad, onError, removeOnUnmount = false, type = 'text/javascript', async = false, defer = false, onUnmount, getInstance, integrity, crossOrigin, beforeLoad })=>{
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [instance, setInstance] = useState(undefined);
    useOnMount(()=>{
        let entry = scriptMap.get(src);
        const handleLoad = (e)=>{
            setLoaded(true);
            onLoad === null || onLoad === void 0 ? void 0 : onLoad(e);
            setInstance(getInstance === null || getInstance === void 0 ? void 0 : getInstance());
        };
        const handleError = (e)=>{
            setError(true);
            onError === null || onError === void 0 ? void 0 : onError(e);
        };
        if (entry) {
            // Increase ref count
            entry.count++;
            if (entry.status === 'loaded') {
                handleLoad(new Event('load'));
            } else if (entry.status === 'error') {
                handleError(new Event('error'));
            } else {
                var _entry_loadListener;
                // script is still loading, add listeners to the queue
                entry.loadListener = (_entry_loadListener = entry.loadListener) !== null && _entry_loadListener !== void 0 ? _entry_loadListener : [];
                var _entry_errorListener;
                entry.errorListener = (_entry_errorListener = entry.errorListener) !== null && _entry_errorListener !== void 0 ? _entry_errorListener : [];
                entry.loadListener.push(handleLoad);
                entry.errorListener.push(handleError);
            }
        } else {
            beforeLoad === null || beforeLoad === void 0 ? void 0 : beforeLoad();
            // Create new script entry
            const script = document.createElement('script');
            script.src = src;
            script.type = type;
            script.async = async;
            script.defer = defer;
            if (integrity) {
                script.integrity = integrity;
            }
            if (!isUndefined(crossOrigin)) {
                script.crossOrigin = crossOrigin;
            }
            entry = {
                script,
                status: 'loading',
                count: 1,
                loadListener: [
                    handleLoad
                ],
                errorListener: [
                    handleError
                ]
            };
            script.onload = (e)=>{
                entry.status = 'loaded';
                var _entry_loadListener;
                const listeners = (_entry_loadListener = entry.loadListener) !== null && _entry_loadListener !== void 0 ? _entry_loadListener : [];
                listeners.forEach((listener)=>listener === null || listener === void 0 ? void 0 : listener(e));
                entry.loadListener = [];
                entry.errorListener = [];
            };
            script.onerror = (e)=>{
                entry.status = 'error';
                var _entry_errorListener;
                const listeners = (_entry_errorListener = entry.errorListener) !== null && _entry_errorListener !== void 0 ? _entry_errorListener : [];
                listeners.forEach((listener)=>listener === null || listener === void 0 ? void 0 : listener(e));
                entry.loadListener = [];
                entry.errorListener = [];
            };
            scriptMap.set(src, entry);
            document.body.appendChild(script);
        }
    });
    useOnUnmount(()=>{
        const entry = scriptMap.get(src);
        if (!entry) return;
        entry.count--;
        if (removeOnUnmount && entry.count === 0) {
            entry.script.remove();
            scriptMap.delete(src);
            onUnmount === null || onUnmount === void 0 ? void 0 : onUnmount();
        }
    });
    return {
        loaded,
        error,
        instance
    };
};
